import { useMemo } from "react"

import { useActiveOrganizationId } from "@/contexts"
import { ApiQueryName } from "@/models/api"
import { useQuery } from "@tanstack/react-query"
import type { UseQueryResult } from "@tanstack/react-query"
import { Interval } from "luxon"

import type { UseApiOptions } from "../types"
import { useRenameQueryResult } from "../utils"
import { useQueryKey } from "../utils/useQueryKey"
import { fetchAvailableReportDates } from "./useAvailableReportDatesUtils"

/**
 * The useAvailableReportDates service/hook returns a range of the available report dates
 * for an organization
 *
 * @returns The available report dates
 * @example
 * const {
 *   availableReportDatesData,
 *   availableReportDatesIsFetched,
 *   availableReportDatesIsPending
 * } = useAvailableReportDates({
 *    queryOptions
 * })
 */
export const useAvailableReportDates = (
  options: UseApiOptions<never, Interval | null> = {}
) => {
  const { queryOptions = {} } = options

  const { activeOrganizationId } = useActiveOrganizationId()

  const queryKey = useQueryKey(
    ApiQueryName.AvailableReportDates,
    "getOne",
    activeOrganizationId,
    {
      ignoreCurrency: true,
    }
  )
  const queryResult = useQuery({
    ...queryOptions,
    enabled: Boolean(activeOrganizationId) && (queryOptions.enabled ?? true),
    queryFn: () => fetchAvailableReportDates(activeOrganizationId ?? ""),
    queryKey,
    // Retrying delays the display of the UI
    // because we're using this data to determine if any org data is available
    retry: false,
  })

  const modifiedQueryResult = useMemo(() => {
    return {
      ...queryResult,
      data: queryResult.data ?? Interval.invalid("Invalid date range"),
    } as UseQueryResult<Interval>
  }, [queryResult])

  return useRenameQueryResult(
    modifiedQueryResult,
    ApiQueryName.AvailableReportDates
  )
}
